import styled from 'styled-components';
import { fontDisplay, fontUi } from '../../../../theme/typography';

export const StyledAutoCard = styled.section`
  width: 100%;
  max-width: 1280px;
  box-sizing: border-box;
  margin: 0 auto 25px;
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: ${({ theme }) => theme.cardRadius || '16px'};
  background: ${({ theme }) => theme.card};
  color: ${({ theme }) => theme.text};
  box-shadow: 0 8px 24px rgba(0, 0, 0, ${({ theme }) => (theme.isDark ? 0.24 : 0.04)});
  overflow: hidden;
`;

export const StyledAutoHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 18px 20px;
  border-bottom: 1px solid ${({ theme }) => theme.borderSubtle};
  > div { min-width: 0; flex: 1; }
  @media (max-width: 600px) { padding: 16px; flex-wrap: wrap; }
`;

export const StyledAutoIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  border-radius: 10px;
  background: ${({ theme }) => theme.hover};
  color: ${({ theme }) => theme.isDark ? theme.headerAccent : theme.success};
  svg { width: 22px; height: 22px; }
`;

export const StyledAutoTitle = styled.h3`
  ${fontDisplay};
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin: 0 0 4px;
  font-size: 18px;
  line-height: 1.2;
  color: ${({ theme }) => theme.text};
`;

export const StyledPatronBadge = styled.span`
  ${fontUi};
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: ${({ theme }) => theme.textMuted};
  font-size: 10px;
  font-weight: 700;
  line-height: 1;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  svg { width: 13px; height: 13px; flex-shrink: 0; }
`;

export const StyledAutoStatus = styled.span`
  ${fontUi};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  flex-shrink: 0;
  min-height: 28px;
  box-sizing: border-box;
  padding: 2px 10px 0;
  border: 1px solid ${({ theme }) => theme.borderSubtle};
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  line-height: 1;
  color: ${({ theme, $active }) => $active ? (theme.isDark ? theme.headerAccent : theme.success) : theme.textMuted};
  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: currentColor;
  }
`;

export const StyledAutoBody = styled.div`
  opacity: ${({ $comingSoon }) => $comingSoon ? 0.65 : 1};
  filter: ${({ $comingSoon }) => $comingSoon ? 'grayscale(1)' : 'none'};
  ${({ $comingSoon }) => $comingSoon && `
    input:disabled, select:disabled, button:disabled { opacity: 1; }
    label { cursor: default; }
  `}
`;

export const StyledAutoSettings = styled.div`
  display: grid;
  grid-template-columns: 1fr 1.15fr 1fr;
  > div + div { border-left: 1px solid ${({ theme }) => theme.borderSubtle}; }
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    > div + div { border-left: 0; border-top: 1px solid ${({ theme }) => theme.borderSubtle}; }
  }
`;

export const StyledAutoField = styled.div`
  padding: 18px 20px;
  min-width: 0;
  @media (max-width: 600px) { padding: 16px; }
`;

export const StyledAutoRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
`;

export const StyledAutoLabel = styled.label`
  ${fontUi};
  font-size: 13px;
  font-weight: 600;
  line-height: 1.4;
  color: ${({ theme }) => theme.text};
  cursor: pointer;
`;

export const StyledAutoSelect = styled.select`
  ${fontUi};
  width: 100%;
  min-width: 0;
  margin-bottom: 8px;
  font-size: 13px;
  height: 36px;
  padding: 0 10px;
  border-radius: ${({ theme }) => theme.controlRadius || '6px'};
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.surfaceAlt};
  color: ${({ theme }) => theme.text};
  cursor: pointer;
  &:focus-visible { outline: 2px solid ${({ theme }) => theme.success}; outline-offset: 2px; }
  &:disabled { opacity: 0.5; cursor: default; }
`;

export const StyledAutoToggle = styled.input.attrs({ type: 'checkbox', role: 'switch' })`
  appearance: none;
  position: relative;
  width: 36px;
  height: 22px;
  margin: 0;
  flex-shrink: 0;
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 20px;
  background: ${({ theme }) => theme.hover};
  cursor: pointer;
  &::after {
    content: '';
    position: absolute;
    top: 3px;
    left: 3px;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: ${({ theme }) => theme.textFaint};
  }
  &:checked {
    background: ${({ theme }) => theme.button};
    border-color: ${({ theme }) => theme.button};
  }
  &:checked::after { transform: translateX(14px); background: ${({ theme }) => theme.buttonText}; }
  &:focus-visible { outline: 2px solid ${({ theme }) => theme.success}; outline-offset: 3px; }
  &:disabled { opacity: 0.5; cursor: default; }
`;

export const StyledAutoFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  padding: 14px 20px;
  border-top: 1px solid ${({ theme }) => theme.borderSubtle};
  background: ${({ theme }) => theme.surfaceAlt};
  > p { max-width: 560px; }
  @media (max-width: 600px) { padding: 16px; flex-direction: column; align-items: stretch; }
`;

export const StyledAutoActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  @media (max-width: 600px) { flex-wrap: wrap; > button { flex: 1; } }
`;

export const StyledAutoButton = styled.button`
  ${fontUi};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
  height: 34px;
  min-height: 34px;
  box-sizing: border-box;
  padding: 2px 12px 0;
  border-radius: ${({ theme }) => theme.controlRadius || '6px'};
  border: 1px solid ${({ theme, $secondary }) => $secondary ? theme.border : theme.button};
  background: ${({ theme, $secondary }) => $secondary ? theme.card : theme.button};
  color: ${({ theme, $secondary }) => $secondary ? theme.text : theme.buttonText};
  cursor: pointer;
  svg { width: 15px; height: 15px; flex-shrink: 0; }
  &:hover:not(:disabled) { filter: brightness(0.94); }
  &:focus-visible { outline: 2px solid ${({ theme }) => theme.success}; outline-offset: 3px; }
  &:disabled { opacity: 0.5; cursor: default; }
`;

export const StyledAutoHint = styled.p`
  ${fontUi};
  margin: 0;
  font-size: 12px;
  line-height: 1.5;
  color: ${({ theme }) => theme.textMuted};
`;

export const StyledAutoResult = styled.div`
  padding: 16px 20px;
  border-top: 1px solid ${({ theme }) => theme.borderSubtle};
  > p { font-weight: 600; margin-bottom: 8px; }
  @media (max-width: 600px) { padding: 16px; }
`;

export const StyledAutoList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

export const StyledAutoItem = styled.li`
  ${fontUi};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 0;
  font-size: 12px;
  line-height: 1.5;
  overflow-wrap: anywhere;
  color: ${({ theme }) => theme.text};
  border-bottom: 1px solid ${({ theme }) => theme.borderSubtle};
  > button { flex-shrink: 0; }
  &:last-child { border-bottom: 0; padding-bottom: 0; }
`;

export const StyledPreviewHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 14px;

  @media (max-width: 600px) {
    flex-direction: column;
    gap: 10px;
  }
`;

export const StyledPreviewTitle = styled.h4`
  ${fontDisplay};
  margin: 0 0 3px;
  color: ${({ theme }) => theme.text};
  font-size: 15px;
  line-height: 1.35;
`;

export const StyledPreviewCounts = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
`;

export const StyledPreviewCount = styled.span`
  ${fontUi};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 24px;
  min-height: 24px;
  box-sizing: border-box;
  padding: 2px 8px 0;
  border: 1px solid ${({ theme, $active }) => $active ? theme.success : theme.border};
  border-radius: 999px;
  background: ${({ theme }) => theme.surfaceAlt};
  color: ${({ theme, $active }) => $active ? (theme.isDark ? theme.headerAccent : theme.success) : theme.textMuted};
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
  white-space: nowrap;
`;

export const StyledPreviewTableWrap = styled.div`
  width: 100%;
  overflow-x: auto;
  border: 1px solid ${({ theme }) => theme.borderSubtle};
  border-radius: ${({ theme }) => theme.controlRadius || '8px'};
`;

export const StyledPreviewTable = styled.table`
  ${fontUi};
  width: 100%;
  min-width: 690px;
  border-collapse: collapse;
  color: ${({ theme }) => theme.text};
  font-size: 12px;
  font-variant-numeric: tabular-nums;

  th {
    padding: 9px 14px;
    border-bottom: 1px solid ${({ theme }) => theme.borderSubtle};
    background: ${({ theme }) => theme.surfaceAlt};
    color: ${({ theme }) => theme.textFaint};
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-align: left;
    text-transform: uppercase;
    white-space: nowrap;
  }

  th:first-child,
  td:first-child {
    width: 18px;
    padding-left: 14px;
    padding-right: 4px;
  }

  th:not(:nth-child(2)),
  td:not(:nth-child(2)) {
    white-space: nowrap;
  }

  td {
    padding: 12px 14px;
    border-bottom: 1px solid ${({ theme }) => theme.borderSubtle};
    vertical-align: middle;
  }

  tbody tr:last-child td { border-bottom: 0; }
  tbody tr:hover { background: ${({ theme }) => theme.hover}; }

  small {
    color: ${({ theme }) => theme.textMuted};
    font-size: 10px;
  }
`;

export const StyledPreviewSkippedRow = styled.tr`
  background: ${({ theme }) =>
    theme.isDark ? 'rgba(237, 66, 69, 0.12)' : 'rgba(233, 66, 53, 0.06)'};
  color: ${({ theme }) => theme.danger};

  &:hover {
    background: ${({ theme }) =>
      theme.isDark ? 'rgba(237, 66, 69, 0.18)' : 'rgba(233, 66, 53, 0.1)'} !important;
  }

  .name {
    color: ${({ theme }) => theme.danger};
  }

  .meta {
    color: ${({ theme }) =>
      theme.isDark ? 'rgba(237, 66, 69, 0.78)' : 'rgba(176, 48, 40, 0.85)'};
  }

  .reason {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    white-space: normal;
    font-size: 12px;
    font-weight: 500;
    line-height: 1.35;
  }

  .reason > button {
    flex-shrink: 0;
  }
`;

export const StyledPreviewPlayer = styled.div`
  ${fontUi};
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;

  .name {
    color: ${({ theme }) => theme.text};
    font-size: 13px;
    font-weight: 600;
    line-height: 1.25;
    white-space: nowrap;
  }

  .meta {
    color: ${({ theme }) => theme.textMuted};
    font-size: 11px;
    font-weight: 500;
    line-height: 1.2;
    white-space: nowrap;
  }
`;

export const StyledPreviewMood = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 20px;
  min-width: 48px;
  box-sizing: border-box;
  padding: 2px 7px 0;
  border-radius: 999px;
  background: ${({ theme }) => theme.hover};
  color: ${({ theme }) => theme.textMuted};
  font-size: 10px;
  font-weight: 600;
  line-height: 1;
`;
